import { useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import moment from "moment";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrButton from "src/design-system/components/GrButton";
import GrCard from "src/design-system/components/GrCard";
import GrCardHeader from "src/design-system/components/GrCardHeader";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrTabs from "src/design-system/components/GrTabs";
import GrStatusPill from "src/design-system/components/GrStatusPill";
import GrBadge from "src/design-system/components/GrBadge";
import GrStatTile from "src/design-system/components/GrStatTile";
import GrFilterChips from "src/design-system/components/GrFilterChips";
import GrListRow from "src/design-system/components/GrListRow";
import GrStatusMark from "src/design-system/components/GrStatusMark";
import GrSparkline from "src/design-system/components/GrSparkline";
import GrPanel from "src/design-system/components/GrPanel";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrText from "src/design-system/components/GrText";
import { GrProgressRing } from "src/design-system/components/GrProgress";
import {
  getJourneyBadge,
  getStepDate,
  getSubmissionAnswerRows,
  isOpenPastDue,
} from "./memberTrackersStorage";
import { isMeasurableQuestion } from "./public/publicJoinStorage";
import {
  formatClock,
  formatMeasure,
  measureKind,
  measureValue,
  unitOf,
} from "./MemberCheckInFields";

/**
 * Member journey page pieces (Design Elements only): status pill, rhythm
 * badge and the Overview / Check-ins / Reports tab bodies.
 */

const NEW_REPORT_DAYS = 7;

/**
 * Check-in status → GrStatusPill status + text. List rows show the text on a
 * grey tag (`rowStatus` when it differs); the answers card keeps the colour fill.
 */
const STEP_PILLS = {
  done: { status: "ok", label: "Checked in" },
  // Sent after its date: "Checked in" with a small "Late" badge.
  late: { status: "ok", label: "Checked in", late: true },
  // Date passed, can still be sent — "Missed" like the ended ones; red only on the answers card.
  overdue: { status: "overdue", rowStatus: "neutral", label: "Missed" },
  missed: { status: "neutral", label: "Missed" },
  open: { status: "due", label: "Due" },
  open_today: { status: "due", label: "Due today" },
  upcoming: { status: "neutral", label: "Upcoming" },
  // P5.5: the coach skipped this check-in (holiday, illness…) — not missed.
  excused: { status: "neutral", label: "Skipped" },
};

/** Check-in status → GrStatusMark in the list row (✓ done, soft red missed, grey upcoming). */
const STEP_MARKS = {
  done: "done",
  late: "done",
  overdue: "missed",
  missed: "missed",
  open: "open",
  open_today: "open",
  upcoming: "upcoming",
  excused: "upcoming",
};

/** Journey status: Missed / Due today / Up to date / Completed / Left / New. */
export const JourneyStatusBadge = ({ summary, size }) => {
  const badge = getJourneyBadge(summary);
  return (
    <GrStatusPill status={badge.status} size={size}>
      {badge.label}
    </GrStatusPill>
  );
};

/** Check-in rhythm, e.g. "Weekly · Thursdays" (a small rectangle, not a status pill). */
const cadenceChipSx = { borderRadius: gr.radius.chip };
export const CadenceChip = ({ label }) =>
  label ? (
    <GrBadge icon={<GrIcon icon={icons.refresh} size="sm" />} sx={cadenceChipSx}>
      {label}
    </GrBadge>
  ) : null;

// Journey page look: titles and the main button in brand teal.
export const journeyTitleSx = { "& h1, & h2, & h3": { color: gr.color.brand } };
export const journeyPrimarySx = {
  "&:not(.Mui-disabled)": { background: gr.color.brand, borderColor: gr.color.brand },
  "&:not(.Mui-disabled):hover": {
    background: gr.color.brandStrong,
    borderColor: gr.color.brandStrong,
  },
};

// ─── Page header + tabs ────────────────────────────────────────────────────

const backLinkSx = {
  color: gr.color.ink2,
  fontWeight: gr.font.weight.regular,
  "&:hover": { color: gr.color.brandDark },
};
const pageHeaderSx = { mb: gr.space[6], alignItems: "flex-start", ...journeyTitleSx };

/** "← All journeys" above a journey page. */
export const JourneyBackLink = ({ onBack }) => (
  <Box sx={{ mb: gr.space[8] }}>
    <GrButton variant="link" onClick={onBack} sx={backLinkSx}>
      ← All journeys
    </GrButton>
  </Box>
);

/** Back link, journey name with its rhythm chip, intro text and the main action. */
export const JourneyHeader = ({ tracker, onBack, actions }) => (
  <>
    <JourneyBackLink onBack={onBack} />
    {/* "What visitors see" — description is the admin's internal note. */}
    <GrPageHeader
      sx={pageHeaderSx}
      title={tracker.name}
      trailing={<CadenceChip label={tracker.cadenceLabel} />}
      subtitle={tracker.visitorMessage || null}
      actions={actions}
    />
  </>
);

const LINE = `1px solid ${gr.color.line}`;
// Phones: tabs take the full row and "Leave journey" drops under them (right).
// Wider: one hairline under the tabs and the link together.
const tabsRowSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "flex-end",
  columnGap: gr.space[4],
  rowGap: gr.space[2],
  mb: gr.space[5],
  borderBottom: { sm: LINE },
};
// Journey tabs look (journey page + journeys list): 3px indicator, grey inactive tabs and counts.
export const journeyTabsLookSx = {
  "& .MuiTabs-indicator": {
    height: 3,
    borderRadius: gr.radius.pill,
    background: gr.color.brand,
  },
  "& .MuiTab-root:not(.Mui-selected)": { color: gr.color.muted },
  "& .MuiTab-root:not(.Mui-selected):hover": { color: gr.color.ink },
  "& .MuiTab-root:not(.Mui-selected) .GrTabs-count": {
    background: gr.color.line,
    color: gr.color.ink2,
  },
};
const tabsSx = {
  flex: { xs: "1 1 100%", sm: "1 1 auto" },
  minWidth: 0,
  borderBottom: { xs: LINE, sm: 0 },
  ...journeyTabsLookSx,
};
// Quiet text link (the dialog asks for a reason before anything happens).
const leaveLinkSx = {
  color: gr.color.danger,
  fontWeight: gr.font.weight.regular,
};

/** Overview / Check-ins / Reports, with "Leave journey" on the right. */
export const JourneyTabs = ({ value, onChange, tabs, onLeave }) => (
  <Box sx={tabsRowSx}>
    <GrTabs
      ariaLabel="Journey"
      value={value}
      onChange={onChange}
      tabs={tabs}
      sx={tabsSx}
    />
    {onLeave ? (
      <GrButton variant="link" onClick={onLeave} sx={leaveLinkSx}>
        Leave journey
      </GrButton>
    ) : null}
  </Box>
);

const isStartStep = (step) => step?.kind === "start" || step?.index === 0;

/** Pill key for one step. */
// A missed check-in that can still be sent (same rule as the Check-in buttons: canSendLate).
const isSendableMissed = (step, summary) =>
  step.state === "missed" && Boolean(summary?.overdue?.some((s) => s.id === step.id));

const stepKey = (step, summary) => {
  if (step.state === "excused") return "excused";
  if (step.state === "done") return step.late ? "late" : "done";
  if (step.state === "missed") return isSendableMissed(step, summary) ? "overdue" : "missed";
  if (step.state === "current") {
    // Still open, but its date has passed.
    if (isOpenPastDue(step)) return "overdue";
    return summary?.open?.id === step.id && summary.openToday
      ? "open_today"
      : "open";
  }
  return "upcoming";
};

// Check-in status as a small tag (chip radius, no glyph): light grey with the status colour
// in a list row; `soft` (answers card title) keeps the status colour's soft fill.
const stepPillSx = {
  alignSelf: "center",
  px: gr.space[2],
  py: "1px",
  border: 0,
  borderRadius: gr.radius.chip,
  fontWeight: gr.font.weight.medium,
  lineHeight: 1.4,
};
const stepPillRowSx = { ...stepPillSx, background: gr.color.bg };
const lateBadgeSx = {
  alignSelf: "center",
  ml: gr.space[1],
  px: gr.space[1],
  py: 0,
  border: 0,
  borderRadius: gr.radius.chip,
  lineHeight: 1.4,
};
const StepPill = ({ step, summary, soft = false }) => {
  const pill = STEP_PILLS[stepKey(step, summary)];
  return (
    <GrStatusPill
      status={soft ? pill.status : pill.rowStatus || pill.status}
      glyph={false}
      sx={soft ? stepPillSx : stepPillRowSx}
    >
      {pill.label}
      {pill.late ? (
        <GrStatusPill status="late" glyph={false} sx={lateBadgeSx}>
          Late
        </GrStatusPill>
      ) : null}
    </GrStatusPill>
  );
};

const stepDateLabel = (step, format) => {
  const date = getStepDate(step);
  return date ? moment(date).format(format) : "";
};

const strongSx = { color: gr.color.brandDark, fontWeight: gr.font.weight.semibold };
// Small spaced capitals: answer labels, result labels and the month headings of the list (journey design).
const capsSx = { letterSpacing: "0.06em", textTransform: "uppercase" };

// ─── Overview ──────────────────────────────────────────────────────────────

const performanceSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "auto minmax(0, 1fr)" },
  alignItems: "center",
  gap: { xs: gr.space[4], sm: gr.space[6] },
};
const ringSx = { display: "flex", justifyContent: { xs: "center", sm: "flex-start" } };
const tilesSx = {
  display: "grid",
  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
  gap: gr.space[3],
};

export const PerformanceCard = ({ tracker, summary }) => {
  const meta = [
    tracker.cadenceLabel,
    summary.startAt ? `Started ${moment(summary.startAt).format("D MMM")}` : "",
    summary.weekTotal
      ? `Week ${Math.min(summary.weekNumber, summary.weekTotal)} of ${summary.weekTotal}`
      : summary.ongoing && summary.dayNumber
        ? `Day ${summary.dayNumber}`
        : "",
    summary.endAt ? `Ends ${moment(summary.endAt).format("D MMM")}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const missedPercent = summary.total
    ? Math.round((summary.missedCount / summary.total) * 100)
    : 0;

  return (
    <GrCard>
      <GrCardHeader
        as="h2"
        title="Your performance"
        subtitle={`${summary.total} check-in${summary.total === 1 ? "" : "s"} in this journey`}
        sx={journeyTitleSx}
        action={meta ? <GrText variant="caption">{meta}</GrText> : null}
        wrapAction
      />
      <Box sx={performanceSx}>
        <Box sx={ringSx}>
          <GrProgressRing
            value={summary.percent}
            secondary={missedPercent}
            size={112}
            stroke={10}
            caption={`${summary.doneCount} of ${summary.total} done`}
            ariaLabel={`Completed ${summary.percent}%, missed ${missedPercent}%`}
          />
        </Box>
        <Box sx={tilesSx}>
          {/* Every check-in the member has sent in this journey (same number as the ring). */}
          <GrStatTile value={summary.doneCount} label="Checked in" />
          {summary.ended ? (
            <GrStatTile value={summary.missedCount} label="Missed" />
          ) : (
            <GrStatTile value={summary.overdue.length} label="Missed" />
          )}
          <GrStatTile value={summary.lateCount} label="Submitted late" />
          <GrStatTile value={summary.upcomingCount} label="Upcoming" />
        </Box>
      </Box>
    </GrCard>
  );
};

const answerFor = (row, question) => {
  const answers = Array.isArray(row?.answers) ? row.answers : [];
  const id = String(question.id || "");
  const label = String(question.label || "").trim().toLowerCase();
  const hit =
    answers.find((a) => id && String(a?.questionId || "") === id) ||
    answers.find(
      (a) => label && String(a?.label || "").trim().toLowerCase() === label
    );
  const value = Array.isArray(hit?.value) ? hit.value.join(", ") : hit?.value;
  return value == null || String(value).trim() === "" ? null : String(value);
};

const TREND_POINTS = 5;
const resultDate = (row) => row.scheduledFor || row.submittedAt;

/**
 * "−0.7 kg since start" (time: "+0:15 min since start"); `better` when it moved
 * the good way (measureDirection "ascending" = less is better).
 */
const sinceStart = (startRaw, nowRaw, question) => {
  const start = measureValue(startRaw, question);
  const now = measureValue(nowRaw, question);
  if (start == null || now == null) return null;
  if (now === start) return { text: "Same as start", better: false };
  const lessIsBetter = String(question?.measureDirection || "") === "ascending";
  const better = lessIsBetter ? now < start : now > start;
  const sign = now > start ? "+" : "−";
  const diff = Math.abs(now - start);
  if (measureKind(question) === "time") {
    return { text: `${sign}${formatClock(diff)}${diff >= 3600 ? "" : " min"} since start`, better };
  }
  const unit = unitOf(question);
  // A "/10" scale shows the plain number (+1 since start); "%" sits on the number.
  const shown = !unit || unit.startsWith("/") ? "" : `${unit === "%" ? "" : " "}${unit}`;
  return { text: `${sign}${Math.round(diff * 100) / 100}${shown} since start`, better };
};

/** The journey's questions (shared + every form), each once; `keep` filters them. */
const journeyQuestions = (tracker, keep) => {
  const seen = new Set();
  return [
    ...(tracker?.sharedQuestions || []),
    ...(tracker?.forms || []).flatMap((form) => form?.questions || []),
  ].filter((q) => {
    if (!q || !keep(q)) return false;
    const key = String(q.id || q.label || "");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// A late check-in counts on its own date.
const byResultDate = (submissions) =>
  [...(submissions || [])].sort(
    (a, b) => new Date(resultDate(a) || 0) - new Date(resultDate(b) || 0)
  );

/**
 * Start → now for each measurable question, the change, and its last five check-in values.
 * `limit`: how many results (0 = all).
 */
const buildJourneyResults = (tracker, submissions, limit = 6) => {
  const questions = journeyQuestions(tracker, isMeasurableQuestion);
  const rows = byResultDate(submissions);
  return questions
    .map((question) => {
      const values = rows
        .map((row) => ({ row, value: answerFor(row, question) }))
        .filter((item) => item.value != null);
      if (!values.length) return null;
      const first = values[0];
      const last = values.length > 1 ? values[values.length - 1] : null;
      return {
        id: String(question.id || question.label),
        label: question.label || "Result",
        start: formatMeasure(first.value, question),
        latest: last ? formatMeasure(last.value, question) : null,
        change: last ? sinceStart(first.value, last.value, question) : null,
        // Only what the member sent (missed check-ins have no value).
        trend: values.slice(-TREND_POINTS).map((item) => ({
          value: measureValue(item.value, question),
          label: `${formatMeasure(item.value, question)} · ${moment(resultDate(item.row)).format("D MMM")}`,
        })),
      };
    })
    .filter(Boolean)
    .slice(0, limit || undefined);
};

export const useJourneyResults = (tracker, submissions, limit = 6) =>
  useMemo(() => buildJourneyResults(tracker, submissions, limit), [tracker, submissions, limit]);

const resultsGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
  gap: gr.space[4],
};
const resultTileSx = { background: gr.color.bg, borderRadius: gr.radius.card };
const resultTitleSx = { fontWeight: gr.font.weight.semibold };
// Start / Now: labels on one line, numbers on one baseline; the sparkline on the right, on the numbers line.
const resultValuesSx = {
  display: "grid",
  gridTemplateColumns: "auto auto minmax(0, 1fr)",
  columnGap: gr.space[4],
  alignItems: "baseline",
  mt: gr.space[3],
};
const resultStartSx = { fontSize: gr.font.size.xxl, color: gr.color.ink2 };
const sparklineSx = { gridColumn: 3, gridRow: 2, justifySelf: "end", alignSelf: "center" };
const changeSx = (better) => ({
  mt: gr.space[3],
  fontWeight: gr.font.weight.semibold,
  color: better ? gr.color.ok : gr.color.muted,
});

export const ResultsCard = ({ results, onAllReports }) => {
  const hasLatest = results.some((item) => item.latest);
  return (
    <GrCard>
      <GrCardHeader
        as="h2"
        title="Your results"
        subtitle={
          hasLatest
            ? "Where you started and where you are now"
            : "Your starting point. You'll see how you've changed after your first check-in."
        }
        sx={journeyTitleSx}
        action={
          onAllReports ? (
            <GrButton variant="link" onClick={onAllReports}>
              All reports →
            </GrButton>
          ) : null
        }
      />
      <Box sx={resultsGridSx}>
        {results.map((item) => (
          <GrPanel key={item.id} sx={resultTileSx}>
            <GrText variant="body" sx={resultTitleSx}>
              {item.label}
            </GrText>
            <Box sx={resultValuesSx}>
              <GrText variant="caption" sx={capsSx}>
                Start
              </GrText>
              <GrText variant="caption" sx={capsSx}>
                {item.latest ? "Now" : ""}
              </GrText>
              <Box />
              <GrText variant="number" sx={item.latest ? resultStartSx : undefined}>
                {item.start}
              </GrText>
              {item.latest ? <GrText variant="number">{item.latest}</GrText> : null}
              <GrSparkline
                points={item.trend}
                ariaLabel={`${item.label}, last ${item.trend.length} check-ins: ${item.trend
                  .map((point) => point.label)
                  .join(", ")}`}
                sx={sparklineSx}
              />
            </Box>
            {item.change ? (
              <GrText variant="secondary" sx={changeSx(item.change.better)}>
                {item.change.text}
              </GrText>
            ) : null}
          </GrPanel>
        ))}
      </Box>
    </GrCard>
  );
};

const hasNoteText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim().length > 0;

const noteHtmlSx = {
  fontFamily: gr.font.family,
  fontSize: gr.font.size.md,
  color: gr.color.ink,
  lineHeight: gr.font.lineHeight.normal,
  "& p": { m: 0, mb: gr.space[2] },
  "& p:last-child": { mb: 0 },
  "& img": { maxWidth: "100%" },
};

// Coach note HTML before it is shown: no scripts, frames or forms, no on… handlers and no
// javascript: / data:text/html links (the browser's own parser, no extra library).
const DROP_TAGS = "script, style, iframe, frame, object, embed, link, meta, base, form";
const URL_ATTRS = /^(href|src|xlink:href|action|formaction)$/i;
const BAD_URL = /^\s*(javascript|vbscript|data:text\/html)/i;
const cleanNoteHtml = (html) => {
  if (typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  doc.body.querySelectorAll(DROP_TAGS).forEach((el) => el.remove());
  doc.body.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach(({ name, value }) => {
      if (/^on/i.test(name) || (URL_ATTRS.test(name) && BAD_URL.test(value))) {
        el.removeAttribute(name);
      }
    });
  });
  return doc.body.innerHTML;
};

/** A coach note: rich text (HTML from the editor) or plain text. */
const NoteBody = ({ html, sx }) =>
  /<\/?[a-z][\s\S]*>/i.test(String(html || "")) ? (
    <Box sx={{ ...noteHtmlSx, ...sx }} dangerouslySetInnerHTML={{ __html: cleanNoteHtml(html) }} />
  ) : (
    <GrText variant="body" sx={{ whiteSpace: "pre-wrap", ...sx }}>
      {html}
    </GrText>
  );

/** Latest submission with a coach note → { submission, step } or null. */
export const findLatestFeedback = (submissions, steps) => {
  const row = [...(submissions || [])]
    .filter((item) => hasNoteText(item.coachNotes))
    .sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0))[0];
  if (!row) return null;
  const step =
    steps.find((item) => String(item.submission?.id || "") === String(row.id)) || null;
  return { submission: row, step };
};

export const FeedbackCard = ({ feedback, onViewCheckIn }) => {
  const { submission, step } = feedback;
  const date = moment(
    step && getStepDate(step) ? getStepDate(step) : submission.submittedAt
  ).format("ddd D MMM");
  const where =
    step && isStartStep(step)
      ? `On your ${step.name || "Start form"} · ${date}`
      : `On your ${date} check-in`;
  return (
    <GrCard>
      <GrCardHeader
        as="h2"
        title="Coach's latest feedback"
        subtitle={where}
        sx={journeyTitleSx}
        action={
          step ? (
            <GrButton variant="link" onClick={() => onViewCheckIn(step.id)}>
              View that check-in →
            </GrButton>
          ) : null
        }
      />
      <GrPanel>
        <NoteBody html={submission.coachNotes} />
      </GrPanel>
    </GrCard>
  );
};

// ─── Check-ins ─────────────────────────────────────────────────────────────

const HIDDEN_ANSWER = (label) => {
  const text = String(label || "").toLowerCase();
  return (
    text.includes("first name") ||
    text.includes("last name") ||
    text === "email" ||
    text.includes("email address") ||
    text.includes("phone")
  );
};

// Answer rows: question on the left, answer on the right, hairline between.
const answerRowSx = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: gr.space[4],
  py: gr.space[3],
  "& + &": { borderTop: `1px solid ${gr.color.line2}` },
};
const answerLabelSx = {
  ...capsSx,
  flex: "1 1 50%",
  minWidth: 0,
  fontWeight: gr.font.weight.medium,
};
const answerValueSx = {
  flex: "1 1 50%",
  minWidth: 0,
  textAlign: "right",
  lineHeight: 1.35,
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
};
const coachNoteSx = { mt: gr.space[4], border: `1px solid ${gr.color.brandSoft}` };
const coachNoteTitleSx = {
  color: gr.color.brandDark,
  fontWeight: gr.font.weight.semibold,
  mb: gr.space[1],
};
const coachNoteTextSx = { fontSize: gr.font.size.sm, color: gr.color.ink2 };

/** One submission's answers + coach note. */
const AnswersBody = ({ submission }) => {
  const answers = getSubmissionAnswerRows(submission).filter(
    (row) => !HIDDEN_ANSWER(row.label)
  );
  return (
    <>
      {answers.length === 0 ? (
        <GrEmptyState compact title="No answers were stored for this check-in." />
      ) : (
        <Box>
          {answers.map((row, index) => (
            <Box key={`${row.label}_${index}`} sx={answerRowSx}>
              <GrText variant="caption" sx={answerLabelSx}>
                {row.label}
              </GrText>
              <GrText variant="body" sx={answerValueSx}>
                {row.value}
              </GrText>
            </Box>
          ))}
        </Box>
      )}
      {hasNoteText(submission.coachNotes) ? (
        <GrPanel sx={coachNoteSx}>
          <GrText variant="secondary" sx={coachNoteTitleSx}>
            Coach note
          </GrText>
          <NoteBody html={submission.coachNotes} sx={coachNoteTextSx} />
        </GrPanel>
      ) : null}
    </>
  );
};

/** Plain "Check-in": the date already tells them apart (no "Check-in 1, 2…"). */
const CHECK_IN_NAME = "Check-in";

/**
 * Card subtitle: the check-in date, plus the time it was sent — or the day
 * and time when it was sent on another day (late).
 */
const submittedLine = (step) => {
  const date = stepDateLabel(step, "ddd D MMM YYYY");
  const sentAt = step.submission?.submittedAt;
  if (!sentAt) return date;
  const sameDay = getStepDate(step) && moment(sentAt).isSame(getStepDate(step), "day");
  return `${date} · ${
    sameDay ? moment(sentAt).format("HH:mm") : `sent ${moment(sentAt).format("D MMM, HH:mm")}`
  }`;
};

/** Shown as "Missed": window closed, or still open but its date has passed. */
const isMissedStep = (step) => step.state === "missed" || isOpenPastDue(step);

/** Done, missed or open now (the rest are still to come). */
const isPastOrOpen = (step) =>
  step.state === "done" ||
  step.state === "missed" ||
  step.state === "current" ||
  step.state === "excused"; // P5.5 skipped by the coach

const checkInsGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", lg: "minmax(320px, 400px) minmax(0, 1fr)" },
  gap: gr.space[6],
  alignItems: "start",
};
// Long journeys: only the list scrolls, the answers card stays beside it.
// 560 ≈ eight rows; a layout height, not a design token.
const listScrollSx = { maxHeight: { lg: 560 }, overflowY: { lg: "auto" } };
const groupLabelSx = {
  ...capsSx,
  mt: gr.space[2],
  mb: gr.space[1],
  px: gr.space[3],
  fontWeight: gr.font.weight.medium,
};
// Hairline between rows, on a wrapper so it stays straight under the selected row's rounded tint.
const rowWrapSx = { "& + &": { borderTop: `1px solid ${gr.color.line2}` } };
// The status tag stays on the right of the row on phones too (outside GrListRow, whose action wraps).
const rowButtonSx = (active) => ({
  display: "flex",
  alignItems: "center",
  gap: gr.space[3],
  width: "100%",
  textAlign: "left",
  borderRadius: gr.radius.tile,
  px: gr.space[3],
  background: active ? gr.color.brandSoft : "transparent",
  boxShadow: active ? `inset 3px 0 0 ${gr.color.brand}` : "none",
  "&:hover": { background: active ? gr.color.brandSoft : gr.color.bg },
  "&.Mui-focusVisible": { boxShadow: gr.shadow.focus },
});
const rowTitleSx = { fontWeight: gr.font.weight.medium };
const rowTextSx = { flex: 1, minWidth: 0 };
// Block, so the meta line is as tall as its own 12px text (not the row's 13px line).
const rowMetaSx = { display: "block" };
const lateBoxSx = {
  background: gr.color.overdueSoft,
  borderRadius: gr.radius.card,
  py: gr.space[6],
};

/** Open-but-late check-in: pink box with the one way forward (submit it now). */
const LateCheckInBox = ({ canAct, onSubmit }) => (
  <GrEmptyState
    sx={lateBoxSx}
    title="You haven't submitted this check-in yet."
    text={
      <Box component="span" sx={{ fontSize: gr.font.size.md }}>
        Submit it now so your coach can review it.
      </Box>
    }
    action={
      canAct ? (
        <GrButton sx={journeyPrimarySx} onClick={onSubmit}>
          Submit check-in
        </GrButton>
      ) : null
    }
  />
);

/** Check-ins tab: every check-in on the left, the chosen one on the right. */
export const CheckInsTab = ({
  steps,
  summary,
  selectedId,
  onSelect,
  onCheckIn,
  canAct,
}) => {
  const [filter, setFilter] = useState("all");
  const detailRef = useRef(null);
  // One column (phones, tablets): the answers card sits under the list, so
  // bring it into view after a tap.
  const select = (stepId) => {
    onSelect(stepId);
    if (window.matchMedia("(max-width: 1199.95px)").matches) {
      window.requestAnimationFrame(() =>
        detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      );
    }
  };
  // Done / missed / open newest first, then the ones still to come (soonest first).
  const { past, ahead } = useMemo(() => {
    const byDate = (a, b) =>
      new Date(getStepDate(a) || 0) - new Date(getStepDate(b) || 0) ||
      a.index - b.index;
    return {
      past: steps.filter(isPastOrOpen).sort(byDate),
      ahead: steps.filter((s) => !isPastOrOpen(s)).sort(byDate),
    };
  }, [steps]);
  const listed = [...past, ...ahead];
  const counts = {
    all: listed.length,
    done: listed.filter((s) => s.state === "done").length,
    missed: listed.filter(isMissedStep).length,
  };
  const shown = listed.filter(
    (s) =>
      filter === "all" ||
      (filter === "missed" ? isMissedStep(s) : s.state === filter)
  );
  // Nothing picked yet: the open check-in, else the latest one done / missed.
  const selected =
    listed.find((s) => s.id === selectedId) ||
    (filter === "all" ? summary.open : null) ||
    (filter === "all" ? past[past.length - 1] : shown[shown.length - 1]) ||
    shown[0] ||
    listed[0] ||
    null;

  const groups = [];
  shown.forEach((step) => {
    const date = getStepDate(step);
    const key = !isPastOrOpen(step)
      ? "Upcoming"
      : date
        ? moment(date).format("MMM YYYY")
        : "";
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.steps.push(step);
    else groups.push({ key, steps: [step] });
  });

  return (
    <Box sx={checkInsGridSx}>
      <GrCard>
        <GrCardHeader
          as="h2"
          title="Your check-ins"
          subtitle="In date order. Select one to see your answers."
          sx={journeyTitleSx}
        />
        <GrFilterChips
          variant="solid"
          ariaLabel="Show check-ins"
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all", label: "All", count: counts.all },
            { value: "done", label: "Checked in", count: counts.done },
            { value: "missed", label: "Missed", count: counts.missed },
          ]}
        />
        {summary.next && getStepDate(summary.next) ? (
          <GrText variant="secondary" sx={{ mt: gr.space[3] }}>
            Next check-in:{" "}
            <Box component="span" sx={strongSx}>
              {moment(getStepDate(summary.next)).format("ddd D MMM YYYY")}
            </Box>
          </GrText>
        ) : null}

        {shown.length === 0 ? (
          <GrEmptyState
            compact
            title={filter === "missed" ? "No missed check-ins — nice work!" : "Nothing here yet."}
          />
        ) : (
          <Box sx={listScrollSx}>
            {groups.map((group) => (
              <Box key={group.key || "nodate"}>
                {group.key ? (
                  <GrText variant="caption" sx={groupLabelSx}>
                    {group.key}
                  </GrText>
                ) : null}
                {group.steps.map((step) => {
                  const key = stepKey(step, summary);
                  const active = selected?.id === step.id;
                  const meta = [
                    isStartStep(step) ? step.name || "Start form" : CHECK_IN_NAME,
                    hasNoteText(step.submission?.coachNotes) ? "Coach replied" : "",
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <Box key={step.id} sx={rowWrapSx}>
                      <ButtonBase
                        component="div"
                        disableRipple
                        aria-pressed={active}
                        onClick={() => select(step.id)}
                        sx={rowButtonSx(active)}
                      >
                        <GrListRow
                          iconPlain
                          icon={<GrStatusMark status={STEP_MARKS[key]} />}
                          title={
                            <Box component="span" sx={rowTitleSx}>
                              {stepDateLabel(step, "ddd D MMM") || step.name}
                            </Box>
                          }
                          meta={
                            <GrText as="span" variant="caption" sx={rowMetaSx}>
                              {meta}
                            </GrText>
                          }
                          sx={rowTextSx}
                        />
                        <StepPill step={step} summary={summary} />
                      </ButtonBase>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        )}
      </GrCard>

      <GrCard ref={detailRef} sx={{ scrollMarginTop: gr.space[12] }}>
        {!selected ? (
          <GrEmptyState compact title="Your check-ins will show here once you start." />
        ) : (
          <>
            <GrCardHeader
              as="h3"
              title={isStartStep(selected) ? selected.name || "Start form" : CHECK_IN_NAME}
              subtitle={submittedLine(selected)}
              action={<StepPill step={selected} summary={summary} soft />}
              sx={journeyTitleSx}
            />

            {selected.submission ? (
              <AnswersBody submission={selected.submission} />
            ) : selected.state === "missed" ? (
              isSendableMissed(selected, summary) ? (
                <LateCheckInBox canAct={canAct} onSubmit={() => onCheckIn(selected.id)} />
              ) : (
                <GrEmptyState
                  compact
                  icon={<GrIcon icon={icons.close} />}
                  title="This check-in was missed."
                  text={summary.ended ? "The journey has ended, so it can't be sent now." : undefined}
                />
              )
            ) : selected.state === "excused" ? (
              <GrEmptyState
                compact
                icon={<GrIcon icon={icons.calendar} />}
                title="Your coach skipped this check-in."
                text="Nothing to do — it does not count as missed."
              />
            ) : selected.state === "current" && isOpenPastDue(selected) ? (
              <LateCheckInBox canAct={canAct} onSubmit={() => onCheckIn(null)} />
            ) : selected.state === "current" ? (
              <GrEmptyState
                compact
                icon={<GrIcon icon={icons.clock} />}
                title="This check-in is open."
                text="Answer it now so your coach can review it."
                action={
                  canAct ? (
                    <GrButton variant="secondary" onClick={() => onCheckIn(null)}>
                      Check-in
                    </GrButton>
                  ) : null
                }
              />
            ) : getStepDate(selected) ? (
              <GrEmptyState
                compact
                icon={<GrIcon icon={icons.calendar} />}
                title="Not open yet"
                text={`Opens on ${stepDateLabel(selected, "ddd D MMM YYYY")}${
                  selected.opensTime ? ` at ${selected.opensTime}` : ""
                }`}
              />
            ) : (
              <GrEmptyState compact title="Nothing submitted for this one." />
            )}
          </>
        )}
      </GrCard>
    </Box>
  );
};

// ─── Reports ───────────────────────────────────────────────────────────────

export const ReportsTab = ({ reports }) => (
  <GrCard>
    <GrCardHeader
      as="h2"
      title="Progress reports"
      subtitle="Shared by your coach. Each one compares your results over a few weeks."
      sx={journeyTitleSx}
    />
    {reports.length === 0 ? (
      <GrEmptyState
        compact
        icon={<GrIcon icon={icons.file} />}
        title="No reports yet. Your coach shares them here as your journey goes on."
      />
    ) : (
      <Box component="ul" sx={{ m: 0, p: 0 }}>
        {reports.map((report, index) => {
          const isNew =
            index === 0 &&
            report.createdAt &&
            moment().diff(moment(report.createdAt), "days") < NEW_REPORT_DAYS;
          return (
            <GrListRow
              key={report.id}
              as="li"
              icon={<GrIcon icon={icons.file} />}
              title={`${report.title || "Progress report"}${
                report.rangeLabel ? ` · ${report.rangeLabel}` : ""
              }`}
              trailing={isNew ? <GrBadge tone="ok">New</GrBadge> : null}
              meta={
                report.createdAt
                  ? `Shared ${moment(report.createdAt).format("D MMM YYYY")}`
                  : ""
              }
              action={
                report.url ? (
                  <GrButton
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(report.url, "_blank", "noopener")}
                  >
                    View PDF
                  </GrButton>
                ) : null
              }
            />
          );
        })}
      </Box>
    )}
  </GrCard>
);
